/// <reference types="jqueryui" />
/// <reference types="jquerymobile" />
// tslint:disable:no-reference
/// <reference path="../../etc/designs/forms/clientlibs/shared/js/libraries/ScForm.ClientValidation.js.d.ts" />
// tslint:enable:no-reference

import { h, patch } from 'bootstrap/load-snabbdom';
import { VNode, VNodeData } from 'snabbdom/vnode';
import $ from 'jquery';
import doT from 'bootstrap/load-dot';
import moment from 'bootstrap/load-moment';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';
import deparam from 'lib/vendor/jquery-deparam-patched';

import SDXModalDialog from 'lib/generic/SDXModalDialog';

import { onlyUnique } from 'lib/util/array';
import { format } from 'lib/util/string';
import { isMobilePage } from 'lib/util/helpers';

import { Votable, Rating, RatingComment } from './Rating';

import { simpleSubscribe, ExposedProductInfo } from './Tc.Module.EcommerceProductDataHub';
import { getProfile } from 'lib/personalization/profile';

function dateCompareRatingDesc(r1 : Rating, r2 : Rating) {
	return r2.getDate().valueOf() - r1.getDate().valueOf();
}

// Constants
const STARS_FILTER_HASH = 'rating-list-stars';

const OCC_RATING_ENDPOINT = '/portal-services/occ/rating/{0}/{1}/';
const ADD_COMMENT_URL = OCC_RATING_ENDPOINT + 'comment/add';
const VOTE_RATING_URL = OCC_RATING_ENDPOINT + 'vote';
const VOTE_COMMENT_URL = OCC_RATING_ENDPOINT + 'comment/{2}/vote';

const VOTE_HELPFUL = 'HELPFUL';
const VOTE_USELESS = 'USELESS';
const VOTE_ABUSE = 'ABUSE';

interface Messages {
	[key : string] : string;
}

interface VotingModel {
	messages : Messages;
	mod : RatingList;
	model : Votable;
	endpoint : string;
}

/**
 * Tealium tracking
 */
function trackingTealium(trackingAction : string) {

	SCS.portal.datalayer.trackEvent(trackingAction, 'r_ratingfilter', 'content', 'Rating');
}

/**
 * Validates comment form.
 * @param $elem jQuery
 */
function validateForm($elem : JQuery) {
	const $form = $elem.closest('.modScForm');
	const $fields = $form.find('.field');
	let validates = true;

	$fields.each((_i, field) => {
		const $field = $(field);
		const val = $field.find('input, textarea').val();

		const clientValidation = $field.data('clientvalidation');
		$field.data('invalid', false);

		if (ScForm.createClientValidation(clientValidation).validate(val).isValid) {
			$field.find('input, textarea').removeClass('error-field');
			$field.removeClass('error');
			$field.find('.error').addClass('hidden');
			$field.data('invalid', false);
		} else {
			$field.find('.error').html(clientValidation.constraintMessage).removeClass('hidden');
			$field.find('input, textarea').addClass('error-field');
			$field.addClass('error');
			$field.data('invalid', true);
			validates = false;
		}
	});

	return validates;
}

function refreshFilterSelect($filterSelectWrapper : JQuery) {
	return $filterSelectWrapper.find('select').resetSS();
}

// Snabbdom render functions
// These could be instance methods but out-sourcing them lets the minifier mingle their names.
function IsUseful({messages, mod, model, endpoint} : VotingModel) {
	if(model.hasVoted()) {
		// The user has already up- or downvoted this item
		return <div attrs-class="rl-useful">{model.getVote() ? messages.upvoteMessage : messages.downvoteMessage}</div>;
	}

	return <div attrs-class="rl-useful">
		<a props-href="#" on-click={[mod.incrementHelpful.bind(mod), endpoint, true, model]}>
			{messages.like} (<span>{model.getUpvotes()}</span>)
		</a>
	</div>;
}

function ReportAbuse({messages, mod, model, endpoint} : VotingModel) {
	if(model.hasSentAbuseReport()) {
		return messages.reportSuccess as any as VNode;
	}

	return <a href="#" on-click={[mod.reportAbuse.bind(mod), endpoint, model]}>{messages.reportAbuse}</a>;
}

function Star({val, rating} : {val : number; rating : number}) {
	return <span attrs-class="stars" class-empty={val > rating}></span>;
}

function SDXStar({val, rating} : {val : number; rating : number}) {
	return <i className={'icon ' + (val <= rating ? 'icon-228-star-filled' : 'icon-082-star')}></i>;
}

export function Stars({rating, sdx} : {rating : number; sdx : boolean}) : VNode[] {
	rating = Math.round(rating);

	return [1, 2, 3, 4, 5].map(function(val) {
		return sdx ? <SDXStar val={val} rating={rating} /> : <Star val={val} rating={rating} />;
	});
}

function Actions(
	{
		mod,
		model,
		endpoint,
	} : {
		mod : RatingList;
		model : Votable;
		endpoint : string;
	},
	...contents : VNode[]
	) {
	const messages = mod.messages;

	contents.push(
		<IsUseful messages={messages} mod={mod} model={model} endpoint={endpoint} />,
		<div attrs-class="rl-report"><ReportAbuse messages={messages} mod={mod} model={model} endpoint={endpoint}/></div>,
	);

	return <div attrs-class="rl-actions cf text-b2">{contents}</div>;
}

function RatingStars({rating} : {rating : number}) {
	return <div attrs-class="rl-rating-stars"><Stars rating={rating} sdx /></div>;
}

function SubcategoryRatings({mod, rating} : {mod : RatingList; rating : Rating}) {
	const subcategoryRatings = rating.getCategorySubratings();

	if(!subcategoryRatings.hasSubratings()) {
		return undefined;
	}

	if(!mod.areSubcategoryRatingsExpanded(rating)) {
		return <a attrs-class="rl-more-link" href="#" on-click={[mod.openSubcategoryRatings.bind(mod), rating]} />;
	}

	return <div attrs-class="rl-rating-subratings">
		<dl attrs-class="text-b2">
			{
				subcategoryRatings.map((ratingValue, category) => [
					<dt>{category.getName()}</dt>,
					<dd><RatingStars rating={ratingValue} /></dd>,
				])
			}
		</dl>
	</div>;
}

function FormField({
	clientValidation,
	field,
	section,
	id,
	label,
	extraProps,
} : {
	clientValidation : ScForm.ValidationData;
	field : string;
	section : string;
	id : string;
	label : string;
	extraProps : VNodeData;
}) {
	return h(section + '.section.cf', [
		<div attrs-class="field" attrs={{'data-clientvalidation': JSON.stringify(clientValidation)}}>
			<label attrs-for={id}>{label}*</label>
			<span attrs-class="inputField">
				{h(field, {
					attrs: {
						'aria-required': true,
					},
					props: {
						id,
						...extraProps,
					},
				})}
			</span>
			<span attrs-class="error hidden" />
		</div>,
	]);
}

function CommentForm({mod, rating} : {mod : RatingList; rating : Rating}) {
	const messages = mod.messages;
	if(rating.hasCommented()) {
		// The user has already commented on this rating, replace the form with a message
		return [<br />, messages.commentMessage];
	}

	return <form on-submit={mod.postComment.bind(mod, rating)}>
		<FormField
			clientValidation={{
				constraintData: {},
				constraintMessage: messages.formCommentConstraint,
				constraintRegex: null,
				constraintType: null,
				foreignValidationModels: {},
				hasConstraint: false,
				hasLengthValidation: true,
				lengthMessage: messages.formCommentConstraint,
				lengthRegex: '^.{10,2500}$',
				maxLength: 2500,
				minLength: 10,
				required: true,
				requiredMessage: messages.formCommentConstraint,
			}}
			field="textarea.textareastandard"
			section="div.textarea.comment"
			id={`rating-comment-${rating.getId()}`}
			label={messages.formCommentLabel}
			extraProps={
				{
					cols: 35,
					name: 'Comment',
					sections: 4,
				}
			}
		/>
		<FormField
			clientValidation={{
				constraintData: {},
				constraintMessage: '',
				constraintRegex: '',
				constraintType: 'REGEX',
				foreignValidationModels: {},
				hasConstraint: true,
				hasLengthValidation: false,
				lengthMessage: 'Length Error',
				lengthRegex: null,
				maxLength: 50,
				minLength: 2,
				required: true,
				requiredMessage: 'Required ',
			}}
			field="input"
			section="div.textfield.user-name"
			id={`rating-user-${rating.getId()}`}
			label={messages.userName}
			extraProps={
				{
					name: 'UserName',
					required: true,
					type: 'text',
					value: '',
				}
			}
		/>
		<div attrs-class="comment-form-buttons">
			<button attrs-class="button button--primary" props-type="submit">{messages.formSave}</button>
		</div>
	</form>;
}

function RatingComments(
	{
		mod,
		rating,
		comments,
		messages,
	} : {
		mod : RatingList;
		rating : Rating;
		comments : RatingComment[];
		messages : Messages;
	},
) {
	return <div attrs-class="rl-rating-comments">
		{
			comments.length ? <ul>
				{
					comments.map(comment => {
						return <li class={{'admin-comment': comment.isAdminComment()}} key={comment.getId()}>
							<div attrs-class="rl-rating-author-time text-sm">
								<span attrs-class="rating-date">
									{SCS.utils.date.format(comment.getDate()) || ''}
								</span>
								{' '}
								{messages.from}
								{' '}
								{comment.isAdminComment() ? <span attrs-class="admin-user">{comment.getName()}</span> : comment.getName()}
							</div>
							<p attrs-class="text-b2">{comment.getComment()}</p>
							<Actions
								mod={mod}
								model={comment}
								endpoint={
									format(
										VOTE_COMMENT_URL,
										rating.getProductId(),
										rating.getId(),
										comment.getId(),
									)
								}
							/>
						</li>;
					})
				}
			</ul> : null
		}
		<div attrs-class="comment-form mod modScForm cf"><CommentForm mod={mod} rating={rating} /></div>
	</div>;
}

/**
 * Rating List
 *
 * @author Zeljko Curcic
 * @namespace Tc.Module
 * @class RatingList
 * @extends Tc.Module
 */
export default class RatingList extends Module {
	// True if mobile view is active
	public isMobile! : boolean;
	// All i18n messages
	public messages! : Messages;
	// Contains all ids as keys whose subrating categories and ratings are currently expanded (only applicable on mobile)
	private subcategoryRatingsVisibleFor : {
		[id : string] : true;
	} = {};
	// A rating object that is currently opened to display its comment list (only one can be open at a time)
	private commentsOpenFor : Rating | null = null;

	// How many entries the list is currently limited to
	private limit : number | undefined = 10;

	private tmplStarsFilter! : (data : any) => string;
	private tmplLanguageFilter! : (data : any) => string;

	private loaded = false;

	private ratings : Rating[] | undefined;

	private rlWrapper! : HTMLDivElement | VNode;
	private $loader! : JQuery;
	private $filterArea! : JQuery;
	private $filters! : JQuery;
	private $starsFilter! : JQuery;
	private $langFilter! : JQuery;

	public on(callback : Hook) {
		this.prepareData();
		this.prepareTemplates();

		this.prepareDOM();
		this.prepareListeners();

		this.loaded = false;

		// Subscribe to EcommerceProductDataHub product updates
		// tslint:disable-next-line:no-unbound-method
		simpleSubscribe(this, this.getRatingLists);

		callback();
	}

	public reportAbuse(endpoint : string, model : Votable, event : MouseEvent) {
		event.preventDefault();

		SDXModalDialog.show({
			buttons: [
				{
					primary: false,
					title: this.$ctx.data('dialogButtonNo'),
					type: 'close',
				},
				{
					click: () => {
						this.doPost(endpoint, {vote: VOTE_ABUSE}, () => {
							model.sendAbuseReport();
							this.display();
						});
					},
					primary: true,
					title: this.$ctx.data('dialogButtonYes'),
					type: 'close',
				},
			],
			displayMode: 'prompt',
			html: this.messages.reportDialogText,
			title: this.messages.reportDialogTitle,
		});
	}

	/**
	 * Post comment
	 * @param $elem jQuery
	 */
	public postComment(rating : Rating, event : Event) {
		event.preventDefault();
		const $form = $<HTMLFormElement>(event.currentTarget as HTMLFormElement);
		if (!validateForm($form)) {
			return;
		}

		const d = deparam($form.serialize());

		const data = {
			comment: d.Comment,
			date: moment().format('YYYY-MM-DDTHH:mm:ssZZ'),
			// FIXME NEC: Route this through
			ipAddress: '0.0.0.0',
			reviewerName: d.UserName,
		};

		const url = format(ADD_COMMENT_URL, rating.getProductId(), rating.getId());

		this.doPost(url, data, () => {
			rating.comment();
			this.display();
		});
	}

	public areSubcategoryRatingsExpanded(rating : Rating) {
		return this.isMobile && !(rating.getId() in this.subcategoryRatingsVisibleFor);
	}

	public openSubcategoryRatings(event : MouseEvent, rating : Rating) {
		event.preventDefault();
		this.subcategoryRatingsVisibleFor[rating.getId()] = true;
		this.display();
	}

	public closeComments() {
		this.commentsOpenFor = null;
		this.display();
	}

	/**
	 * Upvote/downvote a rating or comment
	 * @param $elem jQuery
	 */
	public incrementHelpful(url : string, isHelpful : boolean, model : Votable, event : MouseEvent) {
		event.preventDefault();

		this.doPost(url, {vote: isHelpful ? VOTE_HELPFUL : VOTE_USELESS}, () => {
			model.vote(isHelpful);
			this.display();
		});
	}

	// Displays the current (filtered) ratings
	private display() {
		this.$ctx.addClass('hidden loader-wrap');
		this.$filterArea.addClass('hidden');

		const ratings = this.ratings || [];

		const ratingsFiltered = this.filterRatings(ratings);
		const list = this.renderRatingList(ratingsFiltered, this.limit);
		this.rlWrapper = patch(this.rlWrapper, list);

		this.$loader.remove();

		this.$filterArea.toggleClass('hidden', !ratings.length);
		this.$ctx.toggleClass('hidden', !ratings.length);
		this.$ctx.removeClass('loader-wrap');
	}

	private prepareData() {
		this.isMobile = isMobilePage();
		this.messages = this.$ctx.data();
	}

	private prepareTemplates() {
		this.tmplStarsFilter = doT.template(this.$ctx.find('script.tmpl-stars-filter').html());
		this.tmplLanguageFilter = doT.template(this.$ctx.find('script.tmpl-language-filter').html());
	}

	private prepareDOM() {
		const $ctx = this.$ctx;

		this.rlWrapper = $ctx.find('.rl-wrapper')[0] as HTMLDivElement;

		this.$loader = $ctx.find('.scs-loadingRotator');

		this.$filterArea = this.$ctx.find('.rl-filter');
		this.$filters = this.$filterArea.find('.filter');

		this.$starsFilter = this.$filters.filter('[data-filter="Rating"]');
		this.$langFilter = this.$filters.filter('[data-filter="Language"]');
	}

	private prepareListeners() {
		$(window).on('hashchange', () => {
			if(this.loaded) {
				this.updateFilterFromHash();
				this.display();
			}
		});
	}

	/**
	 * Loads RatingList JSONs for all available product ids and triggers RatingList rendering
	 */
	private getRatingLists(data : ExposedProductInfo) {
		if(this.loaded) {
			// We don’t care about updated varians since only the family contains the rating.
			return;
		}

		const family = data.family;
		const ratings = family.getRatings();
		if(!ratings.loaded()) {
			// Register callback for when rating data is loaded
			// tslint:disable-next-line:no-floating-promises
			ratings.load().done(this.getRatingLists.bind(this, data));

			return;
		}

		// Reset state
		this.limit = 10;
		this.commentsOpenFor = null;
		this.subcategoryRatingsVisibleFor = {};
		this.loaded = true;

		this.ratings = ratings.getRatings() || [];
		this.ratings.sort(dateCompareRatingDesc);

		// Maybe the new ratings allow new values in the filters, update those
		this.displayFilters();
		// Display the ratings
		this.display();
	}

	/**
	 * Add options to filter dropdowns from values existing in RatingList
	 */
	private displayFilters() {
		// Write down all distinct languages for which there are comments
		const languages = (this.ratings || [])
			.map(function(rating) {
				return rating.getLanguage();
			})
			.filter(onlyUnique)
			// Sort by language code
			.sort();

		// Write down all distinct star rating values for which there are comments
		const stars : {
			[rating : number] : true;
		} = {};
		(this.ratings || []).map(function(rating) {
			return rating.getRating();
		}).forEach(function(star) {
			stars[star] = true;
		});

		// Use templates to set the HTML
		this.$starsFilter.html(this.tmplStarsFilter({ratings: stars}));
		this.$langFilter.html(this.tmplLanguageFilter({languages}));

		// Update/re-create custom selects and attach event listeners
		refreshFilterSelect(this.$starsFilter).change(this.filtersChanged.bind(this));
		refreshFilterSelect(this.$langFilter).change(this.filtersChanged.bind(this));

		this.updateFilterFromHash();
	}

	/**
	 * Filter the rating list depending on filter dropdown selections
	 * @param ratings The unfiltered list
	 */
	private filterRatings(ratings : Rating[]) {
		// Assemble all filters (field name/value).
		// Currently there are filters for rating (the number of stars) and comment language.
		const filters = this.$filters.get().map(elem => {
			// Describe each filter
			return {
				field: elem.dataset.filter,
				value: $(elem).find('select').val(),
			};
		}).filter(filter => {
			// Discard filters with unset values
			return !!filter.value;
		});

		// Apply the filters to the ratings collection
		return ratings.filter(rating => {
			for(const filter of filters) {
				const actualValue : string = (rating as any)['get' + filter.field]();
				const filterValue = filter.value;
				if(String(actualValue) !== String(filterValue)) {
					// This filter didn’t match, exclude the rating
					return false;
				}
			}

			// All filters matched (or there were none), include the rating
			return true;
		});
	}

	/**
	 * Render rating list from mod.data.FilteredRatingList
	 * On first rendering initialize filters and more buttons for mobile
	 * @param initial Boolean
	 */
	private renderRatingList(ratingsFiltered : Rating[], limit = Number.MAX_VALUE) {
		const messages = this.messages;

		const ratings = ratingsFiltered.slice(0, limit);
		const hasHidden = ratingsFiltered.length > ratings.length;

		return <div attrs-class="rl-wrapper">
			<ul attrs-class="rating-list">
				{!ratings.length ? <li attrs-class="empty-list">
					<a props-href="#" on-click={this.refreshFilters.bind(this)}>{messages.emptyList}</a>
				</li> : null}
				{
					ratings.map(rating => {
						const comments = rating.getComments();
						const subcategoryRatings = <SubcategoryRatings mod={this} rating={rating} />;

						return <li attrs-class="rating" key={rating.getId()}>
							<RatingStars rating={rating.getRating()} />
							<div attrs-class="rl-rating-body">
								{rating.getHeadline() ? <h3 attrs-class="text-h4 navy">{rating.getHeadline()}</h3> : null}
								<div attrs-class="rl-rating-author-time text-sm">
									<span attrs-class="rating-date">{SCS.utils.date.format(rating.getDate()) || ''}</span>
									{' '}
									{messages.from}
									{' '}
									{rating.getFirstName()}
								</div>
								<p>{rating.getComment()}</p>
								<Actions
									mod={this}
									model={rating}
									endpoint={format(VOTE_RATING_URL, rating.getProductId(), rating.getId())}>
									<a attrs-class="rl-comments-link" on-click={[this.handleClickToComment.bind(this), rating]}>
										<i attrs-class="icon icon-068-reply-message-2 icon--s1"></i>&nbsp;
										{comments.length ? [messages.comments, ' (', comments.length, ')'] : messages.writeComment}
									</a>
								</Actions>
								{this.commentsOpenFor === rating ? <RatingComments
									mod={this}
									rating={rating}
									comments={comments}
									messages={messages}
								/> : null}
							</div>
							{!this.isMobile ? subcategoryRatings : null}
						</li>;
					})
				}
			</ul>
			{
				hasHidden ? <div attrs-class="rl-showall">
					<a props-href="#" attrs-class="button button--secondary" on-click={this.displayAll.bind(this)}>
						{format(messages.displayAll, ratingsFiltered.length)}
					</a>
				</div> : null
			}
		</div>;
	}

	private displayAll(event : MouseEvent) {
		event.preventDefault();
		this.limit = undefined;
		this.display();
	}

	// tslint:disable-next-line:prefer-function-over-method
	private async handleClickToComment(rating : Rating, event : MouseEvent) {
		event.preventDefault();

		const $ctx = this.$ctx;

		// Toggle commentsOpenFor === rating
		this.commentsOpenFor = this.commentsOpenFor === rating ? null : rating;

		const loginRequired = $ctx.data('login-required');
		let isLoggedIn = true;

		if(loginRequired) {
			isLoggedIn = !!(await getProfile()).getUser();
		}

		if(this.commentsOpenFor !== null && !isLoggedIn) {
			// Redirect to login to comment
			const loginUrl = $ctx.data('login-url');
			const $tabNavigation = this.$ctx.closest('.modTabNavigation');
			const $tabNavigationContent = $tabNavigation.find('.modTabNavigationContent').has($ctx[0]);
			const activeTabIndex = $tabNavigation.children('.modTabNavigationContent').index($tabNavigationContent) + 1;

			window.location.href = loginUrl.replace('.tab_index', '.t' + (activeTabIndex || ''));

			return;
		}

		// Do nothing, just display
		this.display();
	}

	private filtersChanged(event : JQuery.ChangeEvent) {
		const $elem = $(event.currentTarget as Element);

		// Tracking
		trackingTealium($elem.data('tracking-action'));

		// Update display
		this.display();
	}

	private updateFilterFromHash() {
		const hashParams = deparam(window.location.hash.substring(1));

		const hashValue = hashParams[STARS_FILTER_HASH];
		this.$starsFilter.find('select').val(hashValue);
		refreshFilterSelect(this.$starsFilter);
	}

	/**
	 * Post data
	 * @param url URL
	 * @param data Data
	 * @param successHandler Success handler
	 */
	private doPost(url : string, data : any, successHandler : () => void) {
		// tslint:disable-next-line:no-floating-promises
		$.ajax({
			contentType: 'application/json',
			data: JSON.stringify(data),
			dataType: 'json',
			error(_xhr, _status, msg) {
				SCS.portal.showError(msg);
			},
			success: successHandler.bind(this) || null,
			type: 'POST',
			url,
		});
	}

	/**
	 * Refresh stars and language select filters.
	 *
	 * @method refreshFilters
	 * @return void
	 */
	private refreshFilters() {
		this.$langFilter.find('select').val('');
		this.$starsFilter.find('select').val('');
		refreshFilterSelect(this.$starsFilter);
		refreshFilterSelect(this.$langFilter);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.RatingList.tsx